/**
 * Beat 8's two sounds.
 *
 * `docs/beat-8-plan.md` writes the brief for both, and the first one contains the
 * whole idea:
 *
 *   **N1** `fuse-throw` — *"one hard mechanical clack, and the room's hum stopping
 *   under it. **The hum stopping is the sound**, not the clack."*
 *   **N2** `hens-panic` — *"the crates opening and the scatter. Dry, close, brief."*
 */

import { connectChain, filter, gain, noiseSource } from '../synth';
import type { SoundDefinition } from '../types';

/**
 * The lever, and then the building stopping.
 *
 * ── The hum is IN this sound, which looks wrong and is the point ────────────
 *
 * The hall already has a running ambience bed. The obvious build is a clack, and
 * to let the bed's own stop do the rest. That does not work, and it is worth
 * writing down why, because the obvious build was tried first: the ambience is
 * faded by the audio engine over a slew that is right for walking out of a room
 * and far too slow for a fuse. What arrives is a clack, and then several hundred
 * milliseconds later a room that has quietly become a different room. The event
 * is the *simultaneity* — the noise stops **because** the lever moved, and if the
 * player does not hear those as one thing there is no cause and effect.
 *
 * So the last third of a second of hum is rendered here and killed on the clack.
 * It sits under the room's real bed for that moment, doubling it, which is
 * inaudible; what is audible is that it lands on the exact sample the lever does.
 *
 * The tail is then **nothing at all for two seconds**, and that silence is the
 * sound. A room that has been humming since beat 3 stops, and the game does not
 * fill the hole.
 */
const fuseThrow: SoundDefinition = {
  id: 'fuse-throw',
  label: 'Fuse thrown',
  description: 'A hard lever clack with the hall hum dying under it, into two seconds of nothing.',
  category: 'foley',
  duration: 2.6,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.85);
    connectChain(out, ctx.destination);

    // ── the hum, and its death ───────────────────────────────────────────────
    //
    // Two partials rather than one: a bare 100 Hz sine reads as a test tone, and
    // what makes a shed hum is the mains fundamental plus the harmonic the
    // cabinet resonates at. Both are cut on the same ramp.
    const humOut = gain(ctx, 0.16);
    connectChain(humOut, out);
    for (const [frequency, level] of [[100, 1], [201, 0.42], [303, 0.15]] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      const level_ = gain(ctx, level);
      connectChain(osc, level_, humOut);
      osc.start(t0);
      osc.stop(t0 + 0.6);
    }
    // Not a `setValueAtTime` cliff. Mains gear coasts for a few tens of
    // milliseconds after its supply goes; an instant cut is a mute button and
    // reads as an audio bug. 45 ms is the difference between a building losing
    // power and a file ending.
    humOut.gain.setValueAtTime(0.16, t0 + 0.3);
    humOut.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.345);
    humOut.gain.setValueAtTime(0, t0 + 0.35);

    // ── the clack ────────────────────────────────────────────────────────────
    //
    // A knife switch is two events 12 ms apart: the lever arriving at its stop
    // and the contacts parting. One transient reads as a click on a mouse.
    for (const [at, level, frequency] of [[0.30, 1, 2400], [0.312, 0.5, 3800]] as const) {
      const shape = gain(ctx, 0);
      connectChain(
        // Already running and looping; the envelope below is what shapes it.
        noiseSource(ctx, 0.09, frequency),
        filter(ctx, { type: 'bandpass', frequency, q: 1.1 }),
        shape,
        out,
      );
      // 1.5 ms of attack. Steel on steel has no rise at all, but a true zero
      // attack in Web Audio is a discontinuity and clips the render.
      shape.gain.setValueAtTime(0, t0 + at);
      shape.gain.linearRampToValueAtTime(0.42 * level, t0 + at + 0.0015);
      shape.gain.exponentialRampToValueAtTime(0.0004, t0 + at + 0.075);
      shape.gain.setValueAtTime(0, t0 + at + 0.08);
    }

    // The box's own resonance under the clack — a low thud from a steel case on a
    // block wall. Without it the switch is happening in mid-air.
    const thud = ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(150, t0 + 0.30);
    thud.frequency.exponentialRampToValueAtTime(58, t0 + 0.40);
    const thudShape = gain(ctx, 0);
    connectChain(thud, thudShape, out);
    thudShape.gain.setValueAtTime(0, t0 + 0.30);
    thudShape.gain.linearRampToValueAtTime(0.30, t0 + 0.308);
    thudShape.gain.exponentialRampToValueAtTime(0.0004, t0 + 0.46);
    thudShape.gain.setValueAtTime(0, t0 + 0.47);
    thud.start(t0 + 0.30);
    thud.stop(t0 + 0.5);

    // Nothing is scheduled after t0 + 0.5 and the render runs to 2.6. That gap is
    // deliberate and is the reason this sound is 2.6 s long rather than 0.6.
  },
};

/**
 * A crate coming open and four birds finding out that they can.
 *
 * ── Dry, close and brief, which mostly means what is NOT here ───────────────
 *
 * No reverb, no tail, no rise. A shed full of birds is a documentary sound and
 * the moment it acquires a swell it becomes a rescue-montage cue, which would
 * make the game's own opinion of this event the opposite of what spec §7.8 says
 * about it — *"they scatter uselessly"*. It stops abruptly because the event
 * stops abruptly.
 *
 * The plastic lid is the loudest thing in it. That is not a mix accident: the
 * crate is the object with the hard surfaces, and putting the lid over the birds
 * is what makes this sound a container being opened rather than an animal noise.
 */
const hensPanic: SoundDefinition = {
  id: 'hens-panic',
  label: 'Crate opened, hens out',
  description: 'A plastic lid thrown back, then four sets of wings, dry and close and over quickly.',
  category: 'foley',
  duration: 1.9,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.8);
    connectChain(out, ctx.destination);

    // ── the lid ──────────────────────────────────────────────────────────────
    //
    // Thin-wall polypropylene: a hard mid clack with almost no low end, and a
    // second lighter one where it lands against the crate behind. `hen-crate.ts`
    // hinges the lid at the rear rim and `blackoutCrates.ts` throws it 2.1 rad,
    // which is a lid that arrives somewhere — so it gets two contacts.
    for (const [at, level] of [[0.0, 1], [0.145, 0.45]] as const) {
      const shape = gain(ctx, 0);
      connectChain(
        noiseSource(ctx, 0.12, 909 + Math.round(at * 1000)),
        filter(ctx, { type: 'bandpass', frequency: 1500, q: 0.8 }),
        // Plastic has a formant and no body. The high shelf is what stops this
        // reading as a wooden box, which the first version did.
        filter(ctx, { type: 'highpass', frequency: 420 }),
        shape,
        out,
      );
      shape.gain.setValueAtTime(0, t0 + at);
      shape.gain.linearRampToValueAtTime(0.38 * level, t0 + at + 0.003);
      shape.gain.exponentialRampToValueAtTime(0.0004, t0 + at + 0.1);
      shape.gain.setValueAtTime(0, t0 + at + 0.11);
    }

    // ── the birds ────────────────────────────────────────────────────────────
    //
    // Four wing bursts, staggered to match the flock's own stagger: `henFlock.ts`
    // lets them out 0.22 s apart because a crate mouth is one bird wide, and the
    // sound agrees with the picture rather than being mixed to a pleasing rhythm.
    //
    // A wingbeat is filtered noise pulsed at about 9 Hz. It is built as an
    // envelope on a band of noise rather than as separate beats because at this
    // rate the individual strokes fuse anyway, and four independent burst trains
    // is sixteen sources for a sound that lasts a second and a half.
    for (let bird = 0; bird < 4; bird += 1) {
      const at = 0.06 + bird * 0.22;
      const span = 0.55 + bird * 0.06;
      const shape = gain(ctx, 0);
      connectChain(
        noiseSource(ctx, span + 0.1, 4001 + bird * 137),
        // Broiler wings are big and slow and move a lot of air. The band sits
        // low for a bird — this is not a sparrow — and the resonance is wide
        // because a wingbeat is a gust, not a pitch.
        filter(ctx, { type: 'bandpass', frequency: 520 - bird * 30, q: 0.55 }),
        shape,
        out,
      );

      // The strokes. Each one is a short swell, and the whole train decays,
      // because a bird that has just got out flaps hardest in the first second
      // and then walks.
      const beats = 5;
      for (let beat = 0; beat < beats; beat += 1) {
        const beatAt = at + beat * (span / beats);
        const decay = 1 - beat / (beats + 1);
        shape.gain.setValueAtTime(0, t0 + beatAt);
        shape.gain.linearRampToValueAtTime(0.11 * decay, t0 + beatAt + 0.03);
        shape.gain.linearRampToValueAtTime(0.004, t0 + beatAt + span / beats - 0.01);
      }
      shape.gain.setValueAtTime(0, t0 + at + span);
    }

    // ── one call, and only one ───────────────────────────────────────────────
    //
    // Hens in a shed do not scream. They make a short flat cluck and then get on
    // with it, and one of those is worth more here than a chorus: a chorus is the
    // sound of animals reacting to the player, which this game does not do
    // anywhere and must not start doing at the moment it says the rescue was
    // useless.
    const call = ctx.createOscillator();
    call.type = 'sawtooth';
    call.frequency.setValueAtTime(430, t0 + 0.34);
    call.frequency.exponentialRampToValueAtTime(300, t0 + 0.40);
    call.frequency.exponentialRampToValueAtTime(365, t0 + 0.47);
    const callShape = gain(ctx, 0);
    connectChain(
      call,
      filter(ctx, { type: 'lowpass', frequency: 1700 }),
      callShape,
      out,
    );
    callShape.gain.setValueAtTime(0, t0 + 0.34);
    callShape.gain.linearRampToValueAtTime(0.07, t0 + 0.365);
    callShape.gain.linearRampToValueAtTime(0.05, t0 + 0.44);
    callShape.gain.exponentialRampToValueAtTime(0.0004, t0 + 0.52);
    callShape.gain.setValueAtTime(0, t0 + 0.53);
    call.start(t0 + 0.34);
    call.stop(t0 + 0.55);
  },
};

export const blackoutSounds: readonly SoundDefinition[] = [fuseThrow, hensPanic];
